import React, { useEffect, useMemo, useRef, useState } from 'react';

interface FeatureSectionProps {
  children?: React.ReactNode;
  onSelectTexture?: (textureName: string) => void;
  selectedTexture?: string | null;
}

export const FeatureSection: React.FC<FeatureSectionProps> = ({
  children,
  onSelectTexture,
  selectedTexture = null
}) => {
  const darkNavy = '#2c3e50';
  const emeraldGreen = '#58a08c';

  const [textures, setTextures] = useState<string[]>([]);
  const [activeTexture, setActiveTexture] = useState<string | null>(selectedTexture ?? null);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [pressing, setPressing] = useState<string | null>(null);

  const stripViewportRef = useRef<HTMLDivElement | null>(null);
  const innerStripRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ down: boolean; startX: number; startScroll: number }>({ down: false, startX: 0, startScroll: 0 });

  // responsive counts / sizes
  const visibleCount = isMobile ? 3 : 4;
  const thumbSize = isMobile ? 56 : 80;
  const gap = 12;
  const viewportWidth = visibleCount * thumbSize + (visibleCount - 1) * gap;

  // handle resize -> mobile flag
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // load manifest once
  useEffect(() => {
    let mounted = true;
    fetch(`${process.env.PUBLIC_URL}/textures/manifest.json`)
      .then((res) => res.json())
      .then((data: string[]) => {
        if (!mounted) return;
        setTextures(data || []);
        // set default only if parent didn't provide and no local active
        if (!selectedTexture && !activeTexture && data && data.length > 0) {
          setActiveTexture(data[0]);
          onSelectTexture?.(data[0]);
          // DON'T auto-scroll here: user requested to keep slider position unchanged on click/selection
        }
      })
      .catch((err) => console.error('Erreur chargement textures:', err));
    return () => {
      mounted = false;
    };
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync when parent forces selection (do not auto-scroll)
  useEffect(() => {
    if (selectedTexture && selectedTexture !== activeTexture) {
      setActiveTexture(selectedTexture);
      // Intentionally DO NOT call scrollIntoView here to preserve user's current scroll position.
    }
  }, [selectedTexture, activeTexture]);

  const handleSelectTexture = (texture: string) => {
    setActiveTexture(texture);
    onSelectTexture?.(texture);
    // Intentionally DO NOT auto-scroll so slider remains where the user left it.
  };

  // arrows scroll by a free amount (no snapping)
  const scrollByAmount = (dir: 'left' | 'right') => {
    const el = stripViewportRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.6, viewportWidth); // scroll by 60% of visible or at least viewport
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  // pointer dragging to scroll (desktop + touch)
  useEffect(() => {
    const el = stripViewportRef.current;
    if (!el) return;

    const onPointerDown = (e: PointerEvent) => {
      drag.current.down = true;
      drag.current.startX = e.clientX;
      drag.current.startScroll = el.scrollLeft;
      (e.target as Element).setPointerCapture?.((e as any).pointerId);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!drag.current.down) return;
      const dx = e.clientX - drag.current.startX;
      el.scrollLeft = drag.current.startScroll - dx;
    };
    const onPointerUp = (e: PointerEvent) => {
      drag.current.down = false;
      try { (e.target as Element).releasePointerCapture?.((e as any).pointerId); } catch {}
    };

    el.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [viewportWidth]);

  // wheel horizontal scrolling (mouse wheel)
  useEffect(() => {
    const el = stripViewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // prefer vertical wheel -> translate to horizontal scroll
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // keyboard navigation for accessibility (Left/Right while focused)
  useEffect(() => {
    const el = innerStripRef.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        scrollByAmount('right');
      } else if (e.key === 'ArrowLeft') {
        scrollByAmount('left');
      }
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [viewportWidth]);

  const thumbnailStyle = useMemo(
    () =>
      ({
        width: thumbSize,
        height: thumbSize,
        flex: `0 0 ${thumbSize}px`,
        borderRadius: 12,
        padding: 0
      } as React.CSSProperties),
    [thumbSize]
  );

  return (
    <section
      id="feature"
      style={{
        height: isMobile ? 'auto' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '18px 6%' : '0 5%',
        // allow interaction on desktop
        pointerEvents: 'auto',
        zIndex: 10
      }}
    >
      <div
        style={{
          maxWidth: isMobile ? '100%' : '900px',
          width: '100%',
          textAlign: 'center',
          pointerEvents: 'auto',
          background: isMobile ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.6)',
          padding: isMobile ? '14px' : '28px',
          borderRadius: '20px',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 14px 40px rgba(0,0,0,0.06)'
        }}
      >
        <h1
          style={{
            margin: '0 0 14px 0',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            color: emeraldGreen,
            fontSize: isMobile ? '1.2rem' : '1.9rem'
          }}
        >
          Besoin d'automatiser l'inspection qualité
          <br />
          <span style={{ fontFamily: 'X360, sans-serif', color: 'white', WebkitTextStroke: `0.01px ${darkNavy}` }}>
            IA SUR MESURE
          </span>
        </h1>

        <div style={{ margin: isMobile ? '10px 0' : '18px 0' }}>{children}</div>

        <div style={{ height: '1px', background: '#eee', margin: isMobile ? '12px auto' : '16px auto', width: '80%' }} />

        <div>
          <p
            style={{
              fontWeight: 700,
              color: darkNavy,
              marginBottom: isMobile ? 10 : 16,
              fontSize: '0.88rem',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            Option 2: IA Autonome sur tout type de tissu (Détection Anomalies)
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {/* Left arrow */}
            <button
              onClick={() => scrollByAmount('left')}
              aria-label="Précédent"
              style={{
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48,
                borderRadius: '50%',
                border: 'none',
                background: emeraldGreen,
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 6px 18px rgba(88,160,140,0.18)'
              }}
              type="button"
            >
              ←
            </button>

            {/* Viewport (scrollable) */}
            <div
              ref={stripViewportRef}
              role="listbox"
              aria-label="Sélecteur de textures"
              tabIndex={0}
              style={{
                display: 'flex',
                gap,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollBehavior: 'smooth',
                width: viewportWidth,
                paddingBottom: 6,
                msOverflowStyle: 'none',
                scrollbarWidth: 'none'
              }}
            >
              {/* hide scrollbar for webkit */}
              <style>{`
                div[role="listbox"]::-webkit-scrollbar { display: none; height: 10px; }
                div[role="listbox"] { -webkit-overflow-scrolling: touch; }
              `}</style>

              <div
                ref={innerStripRef}
                style={{
                  display: 'flex',
                  gap,
                  alignItems: 'center',
                  padding: '6px 4px'
                }}
              >
                {textures.map((texture) => {
                  const isActive = texture === activeTexture;
                  const isPressing = pressing === texture;
                  return (
                    <button
                      key={texture}
                      data-filename={texture}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleSelectTexture(texture)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectTexture(texture);
                        }
                      }}
                      onMouseDown={() => setPressing(texture)}
                      onMouseUp={() => setPressing(null)}
                      onMouseLeave={() => setPressing(null)}
                      type="button"
                      style={{
                        ...thumbnailStyle,
                        border: isActive ? `3px solid ${emeraldGreen}` : '2px solid rgba(0,0,0,0.06)',
                        boxShadow: isActive
                          ? `0 6px 20px ${emeraldGreen}33, inset 0 1px 0 rgba(255,255,255,0.5)`
                          : isPressing
                          ? '0 6px 18px rgba(0,0,0,0.12) inset'
                          : '0 6px 14px rgba(2,6,23,0.06)',
                        background: '#fff',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        transition: 'transform 140ms ease, box-shadow 180ms ease, border 120ms ease',
                        transform: isPressing ? 'translateY(2px) scale(0.995)' : 'translateY(0) scale(1)',
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/textures/${texture}`}
                        alt={texture}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          pointerEvents: 'none'
                        }}
                      />
                      {isActive && (
                        <span
                          aria-hidden
                          style={{
                            position: 'absolute',
                            right: 6,
                            top: 6,
                            width: 18,
                            height: 18,
                            borderRadius: 6,
                            background: emeraldGreen,
                            color: 'white',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(88,160,140,0.25)'
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right arrow */}
            <button
              onClick={() => scrollByAmount('right')}
              aria-label="Suivant"
              style={{
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48,
                borderRadius: '50%',
                border: 'none',
                background: emeraldGreen,
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 6px 18px rgba(88,160,140,0.18)'
              }}
              type="button"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};